import { HttpStatusCode } from '@angular/common/http';
import {
  computed,
  inject,
  Injectable,
  Signal,
  signal,
  WritableSignal,
} from '@angular/core';
import { Router } from '@angular/router';
import { NotificationService } from '@appkit4/angular-components/notification';
import { AuthService } from '@core/services/auth.service';
import { SessionStorageService } from '@core/session-storage.service';
import { TranslateService } from '@ngx-translate/core';
import { AccessTokenDto } from '@shared/dtos/access-token.dto';
import { notificationDataOptions } from '@shared/helpers/static/notification-data-options';
import { CurrentUserInfo } from '@shared/models';
import { jwtDecode } from 'jwt-decode';
import { catchError, map, Observable, of, throwError } from 'rxjs';
import {
  AccessTokenCodeRequest,
  AccessTokenCodeResponse,
  OAuthLoginSessionRequest,
  ValidationProblemDetails,
} from 'src/app/web-api-client';
import { CountryAdminPermissionsService } from './country-admin-permissions.service';

@Injectable({
  providedIn: 'root',
})
export class CurrentUserService {
  private readonly notificationService = inject(NotificationService);
  private readonly router = inject(Router);
  private readonly translate = inject(TranslateService);
  private readonly session = inject(SessionStorageService);
  private readonly auth = inject(AuthService);
  private readonly countryAdminPermissionsService = inject(
    CountryAdminPermissionsService
  );

  POST_LOGIN_REDIRECT_URL_KEY = 'postLoginRedirectUrl';

  /**
   * Current User information
   */
  public currentUserInfo: WritableSignal<CurrentUserInfo | undefined> =
    signal(undefined);

  /**
   * Current User's initials
   */
  public userInitials: Signal<string> = computed(
    () => this.currentUserInfo()?.userInitials ?? ''
  );

  /**
   * Whether the current user has admin privileges.
   */
  public isAdmin: Signal<boolean> = computed(() =>
    this.currentUserInfo()?.isAdmin ? true : false
  );

  /**
   * Whether the current user has the Country Admin role (from permissions API / JWT).
   */
  public isCountryAdmin: WritableSignal<boolean> = signal(false);

  /**
   * Country IDs the Country Admin may mutate.
   */
  public countryAdminEditScope: WritableSignal<number[]> = signal([]);

  /**
   * Elevated users (Gstar/System Admin) may edit any country.
   */
  public canEditAnyCountry: WritableSignal<boolean> = signal(false);

  /**
   * True after the Country Admin permissions request has completed (success or fallback).
   */
  public countryAdminPermissionsReady: WritableSignal<boolean> = signal(false);

  isRestrictedCountryAdmin(): boolean {
    return this.isCountryAdmin() && !this.isAdmin();
  }

  defaultAuthenticatedRoute(): string {
    return this.isRestrictedCountryAdmin() ? '/country' : '/dashboard';
  }

  private permissionsLoaded = false;
  private permissionsRequestInFlight = false;

  checkCurrentUser(): void {
    const sessionData: AccessTokenCodeResponse = JSON.parse(
      this.session.get('session')
    );
    this.initCurrentUser(sessionData);
  }

  handleInvalidAccessToken(message: string): void {
    this.stripOAuthReturnQueryFromUrl();
    this.clearCountryAdminPermissions();
    this.notificationService.show(
      this.translate.instant('login.loginErrorTitle'),
      message,
      '',
      '',
      notificationDataOptions
    );
    this.router.navigate(['/login']);
  }

  completeOAuthLoginSession(loginSessionId: string): Observable<boolean> {
    const request = new OAuthLoginSessionRequest({ loginSessionId });
    return this.auth.completeOAuthLoginSession(request).pipe(
      map((response: AccessTokenCodeResponse) => {
        this.session.set('session', JSON.stringify(response));
        this.initCurrentUser(response);
        this.stripOAuthReturnQueryFromUrl();
        return true;
      }),
      catchError((errorDetails: ValidationProblemDetails) => {
        this.handleInvalidAccessToken(
          errorDetails?.title ||
            this.translate.instant('login.loginErrorDescription')
        );
        return of(false);
      })
    );
  }

  getAccessToken(request: AccessTokenCodeRequest): Observable<boolean> {
    return this.auth.getAccessToken(request).pipe(
      catchError((errorDetails: ValidationProblemDetails) =>
        this.errorHandler(errorDetails)
      ),
      map((response: AccessTokenCodeResponse) => {
        this.session.set('session', JSON.stringify(response));
        this.initCurrentUser(response);
        this.stripOAuthReturnQueryFromUrl();
        return true;
      })
    );
  }

  /**
   * True when the user may create/update/import country content for the given country.
   */
  canEditCountry(countryId: number | null | undefined): boolean {
    if (
      countryId === null ||
      countryId === undefined ||
      Number.isNaN(Number(countryId))
    ) {
      return false;
    }

    const id = Number(countryId);

    if (this.isAdmin()) {
      return true;
    }

    // For a Country Admin the assigned countries always win over a broader API flag
    if (this.isCountryAdmin()) {
      return this.countryAdminEditScope().includes(id);
    }

    return this.canEditAnyCountry();
  }

  loadCountryAdminPermissions(force = false): void {
    if (!this.currentUserInfo()) {
      return;
    }

    if (this.permissionsRequestInFlight) {
      return;
    }

    if (this.permissionsLoaded && !force) {
      return;
    }

    this.permissionsRequestInFlight = true;

    this.countryAdminPermissionsService.get().subscribe({
      next: permissions => {
        this.isCountryAdmin.set(
          permissions.isCountryAdmin || !!this.currentUserInfo()?.isCountryAdmin
        );
        this.countryAdminEditScope.set(permissions.countryAdminEditScope);
        this.canEditAnyCountry.set(permissions.canEditAnyCountry);
        this.permissionsLoaded = true;
        this.permissionsRequestInFlight = false;
        this.countryAdminPermissionsReady.set(true);
      },
      error: () => {
        this.applyJwtCountryAdminFallback();
        this.permissionsLoaded = true;
        this.permissionsRequestInFlight = false;
        this.countryAdminPermissionsReady.set(true);
      },
    });
  }

  private navigateToPostLoginUrl(): void {
    const redirectUrl = this.session.get(this.POST_LOGIN_REDIRECT_URL_KEY);
    this.session.remove(this.POST_LOGIN_REDIRECT_URL_KEY);

    if (redirectUrl?.startsWith('/')) {
      if (
        this.isRestrictedCountryAdmin() &&
        (redirectUrl === '/' || redirectUrl.startsWith('/dashboard'))
      ) {
        this.router.navigate(['/country']);
        return;
      }
      this.router.navigateByUrl(redirectUrl);
      return;
    }

    this.router.navigate([this.defaultAuthenticatedRoute()]);
  }

  private initCurrentUser(accessTokenCode: AccessTokenCodeResponse): void {
    if (!accessTokenCode || !accessTokenCode.accessToken) {
      return this.handleInvalidAccessToken(
        this.translate.instant('login.loginErrorDescription')
      );
    }

    const accessToken: AccessTokenDto = jwtDecode<AccessTokenDto>(
      accessTokenCode.accessToken
    );
    const userInfo = new CurrentUserInfo(accessToken);
    this.currentUserInfo.set(userInfo);
    if (!this.permissionsLoaded) {
      this.applyJwtCountryAdminFallback(userInfo);
    }
    this.loadCountryAdminPermissions();
  }

  private applyJwtCountryAdminFallback(userInfo?: CurrentUserInfo): void {
    const info = userInfo ?? this.currentUserInfo();
    this.isCountryAdmin.set(!!info?.isCountryAdmin);
    this.canEditAnyCountry.set(!!info?.isAdmin);
    // Scope is only available from the permissions API
    if (!this.permissionsLoaded) {
      this.countryAdminEditScope.set([]);
    }
  }

  private clearCountryAdminPermissions(): void {
    this.isCountryAdmin.set(false);
    this.countryAdminEditScope.set([]);
    this.canEditAnyCountry.set(false);
    this.countryAdminPermissionsReady.set(false);
    this.permissionsLoaded = false;
    this.permissionsRequestInFlight = false;
  }

  private errorHandler(
    errorDetails: ValidationProblemDetails
  ): Observable<never> {
    if (errorDetails.status === HttpStatusCode.InternalServerError) {
      this.router.navigate(['/server-error']);
    }
    return throwError(() => {
      this.handleInvalidAccessToken(errorDetails.title!);
    });
  }

  private stripOAuthReturnQueryFromUrl(): void {
    if (!location.search) {
      return;
    }

    history.replaceState(null, '', `${location.pathname}${location.hash}`);
  }
}
